// Chantier 4 — Parseur du fichier Excel de chiffrage (.xlsx, onglet "Meuble").
// Parsing effectué côté navigateur avec SheetJS (xlsx), aucune dépendance backend pour la
// LECTURE du fichier. Le résultat structuré est persisté dans settings.pricing_grid_import
// et lu par backend/pricing_excel_grid.py pour alimenter en direct le moteur de prix
// (compute_price() dans server.py : Caisson, Séparation-vertical, Porte).
import * as XLSX from "xlsx";

// Titres de section recherchés dans la colonne A, dans l'ordre où ils apparaissent.
const SECTION_TITLES = [
  "Caisson",
  "Séparation-vertical",
  "Dos",
  "Tablette mobile",
  "Tablette fixe",
  "Barre de penderie",
  "Tiroir",
  "Porte",
  "Plinthe",
  "Fileur et contre fileur",
];

function toNum(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(",", "."));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function isRangeLabel(v) {
  return typeof v === "string" && v.trim().toLowerCase().startsWith("de ");
}

function rangeMin(v) {
  const m = v.trim().match(/de\s+([\d.,]+)/i);
  return m ? toNum(m[1]) : null;
}

function cellAt(grid, r, c) {
  return grid[r] ? (grid[r][c] ?? null) : null;
}

function findSectionAnchors(grid) {
  const anchors = [];
  for (let r = 0; r < grid.length; r++) {
    const v = cellAt(grid, r, 0);
    if (typeof v !== "string") continue;
    const trimmed = v.trim();
    if (SECTION_TITLES.includes(trimmed) && !anchors.some((a) => a.row === r)) {
      anchors.push({ title: trimmed, row: r });
    }
  }
  anchors.sort((a, b) => a.row - b.row);
  return anchors;
}

function readColumnRanges(grid, labelRow, boundRow, startCol, maxCol) {
  const out = [];
  let c = startCol;
  while (c <= maxCol && isRangeLabel(cellAt(grid, labelRow, c))) {
    out.push({ min: rangeMin(cellAt(grid, labelRow, c)), max: toNum(cellAt(grid, boundRow, c)), col: c });
    c++;
  }
  return out;
}

function readColumnNumbers(grid, row, startCol, maxCol) {
  const out = [];
  let c = startCol;
  while (c <= maxCol && typeof cellAt(grid, row, c) === "number") {
    out.push({ value: cellAt(grid, row, c), col: c });
    c++;
  }
  return out;
}

// Lit la matrice "ligne = plage/valeur" × "colonnes déjà repérées" commençant à startRow,
// tant que la colonne B contient une plage ("De X à") ou que la colonne C contient une
// valeur numérique discrète (ex: Plinthe "Largeur" = 80, 100 sans plage, avec ou sans
// l'étiquette d'axe "Largeur" encore présente en colonne B sur la première ligne).
function readRowAxisAndPrices(grid, startRow, cols) {
  const axisValues = [];
  const prices = [];
  let r = startRow;
  while (r < grid.length) {
    const labelCell = cellAt(grid, r, 1);
    const altCell = cellAt(grid, r, 2);
    let entry = null;
    if (isRangeLabel(labelCell)) {
      entry = { min: rangeMin(labelCell), max: toNum(altCell) };
    } else if (typeof altCell === "number" && !isRangeLabel(labelCell)) {
      // Cas "Plinthe" : une seule valeur numérique en colonne C, pas de plage en B.
      entry = { value: altCell };
    }
    if (!entry) break;
    const rowPrices = cols.map((c) => toNum(cellAt(grid, r, c.col)));
    if (rowPrices.every((p) => p === null)) break;
    axisValues.push(entry);
    prices.push(rowPrices);
    r++;
  }
  return { axisValues, prices, endRow: r };
}

// Repère, à droite de la matrice de prix d'un bloc, le premier petit tableau
// "composant / quantité / prix unitaire / total" lié à ce même bloc (même bande de lignes).
function readHardwareBreakdown(grid, startRow, endRow, fromCol) {
  const items = [];
  for (let r = startRow; r < endRow && r < grid.length; r++) {
    for (let c = fromCol; c < fromCol + 40 && c < 45; c++) {
      const label = cellAt(grid, r, c);
      if (typeof label !== "string" || !label.trim()) continue;
      const qty = toNum(cellAt(grid, r, c + 1));
      const unit = toNum(cellAt(grid, r, c + 2));
      const total = toNum(cellAt(grid, r, c + 3));
      if (qty !== null && unit !== null) {
        items.push({ composant: label.trim(), quantite: qty, prix_unitaire: unit, total: total });
      }
      break; // un seul tableau par ligne (le premier trouvé en partant de fromCol)
    }
  }
  return items;
}

// Extraction générique "bloc(s) couplé(s)" : un ou plusieurs groupes profondeur (empilés
// verticalement ou juxtaposés horizontalement sur la même ligne d'en-tête), chacun couplant
// une plage de profondeur à une matrice colonnes (Largeur/Nombre/Profondeur tiroir) × lignes
// (Hauteur/Largeur) dont les cellules sont déjà le prix de vente final HT — et son petit
// tableau de quincaillerie.
function extractCoupledBlocks(grid, startRow, endRowExclusive) {
  const blocks = [];
  for (let r = startRow; r < endRowExclusive; r++) {
    for (let p = 0; p < 45; p++) {
      if (cellAt(grid, r, p) !== "Profondeur") continue;
      let axisCol = null;
      let axisLabel = null;
      let labelOnRangeRow = false;
      for (let c = p + 1; c < p + 10 && c < 45; c++) {
        const v = cellAt(grid, r, c);
        if (v === "Largeur" || v === "Nombre" || v === "Profondeur tiroir") { axisCol = c; axisLabel = v; break; }
      }
      const rangeRow = r + 1;
      // Variante "Tiroir" : l'étiquette d'axe colonnes est sur la même ligne que la plage
      // de profondeur (rangeRow), pas sur la ligne "Profondeur" elle-même.
      if (axisCol === null) {
        for (let c = p + 2; c < p + 10 && c < 45; c++) {
          const v = cellAt(grid, rangeRow, c);
          if (v === "Largeur" || v === "Nombre" || v === "Profondeur tiroir") { axisCol = c; axisLabel = v; labelOnRangeRow = true; break; }
        }
      }
      if (axisCol === null) continue;
      const depthLabel = cellAt(grid, rangeRow, p);
      if (!isRangeLabel(depthLabel)) continue;
      const depth = { min: rangeMin(depthLabel), max: toNum(cellAt(grid, rangeRow, p + 1)) };
      const valuesRow = labelOnRangeRow ? rangeRow + 1 : rangeRow;
      let cols;
      if (axisLabel === "Largeur") {
        cols = readColumnRanges(grid, valuesRow, valuesRow + 1, axisCol, axisCol + 15);
      } else {
        cols = readColumnNumbers(grid, valuesRow, axisCol, axisCol + 15).map((n) => ({ ...n, min: n.value, max: n.value }));
      }
      if (!cols.length) continue;
      const heightStartRow = labelOnRangeRow ? valuesRow + 1 : valuesRow + 2;
      const { axisValues, prices, endRow } = readRowAxisAndPrices(grid, heightStartRow, cols);
      if (!axisValues.length) continue;
      const hardware = readHardwareBreakdown(grid, heightStartRow, endRow + 12, cols[cols.length - 1].col + 2);
      blocks.push({
        profondeur: depth,
        axe_colonnes: axisLabel === "Largeur" ? "largeur" : axisLabel === "Profondeur tiroir" ? "profondeur_tiroir" : "nombre",
        axe_lignes: "hauteur_ou_largeur",
        colonnes: cols.map((c) => (axisLabel === "Largeur" ? { min: c.min, max: c.max } : c.value)),
        lignes: axisValues,
        prix: prices,
        quincaillerie: hardware,
      });
    }
  }
  return blocks;
}

// Fallback pour les sections sans "Profondeur" (ex: Barre de penderie, Porte, Plinthe,
// Fileur et contre fileur) : une seule matrice colonnes (Nombre/Largeur/Longueur, en plages
// ou valeurs discrètes) × lignes (Hauteur/Largeur), sans regroupement par profondeur.
function extractSingleAxisBlock(grid, startRow, endRowExclusive) {
  for (let r = startRow; r < endRowExclusive; r++) {
    for (let c = 0; c < 45; c++) {
      const label = cellAt(grid, r, c);
      if (label !== "Nombre" && label !== "Largeur" && label !== "Longueur") continue;
      const valueRow = r + 1;
      let cols;
      if (isRangeLabel(cellAt(grid, valueRow, c))) {
        cols = readColumnRanges(grid, valueRow, valueRow + 1, c, c + 15);
      } else {
        cols = readColumnNumbers(grid, valueRow, c, c + 15).map((n) => ({ ...n, min: n.value, max: n.value }));
      }
      if (!cols.length) continue;
      const heightStartRow = r + 3; // valueRow(+1) + ligne de bornes/unité "cm"(+1)
      const { axisValues, prices, endRow } = readRowAxisAndPrices(grid, heightStartRow, cols);
      if (!axisValues.length) continue;
      const hardware = readHardwareBreakdown(grid, heightStartRow, endRow + 12, cols[cols.length - 1].col + 2);
      return [{
        profondeur: null,
        axe_colonnes: label.toLowerCase(),
        colonnes: cols.map((n) => (n.min !== undefined && n.max !== undefined && n.min !== n.value ? { min: n.min, max: n.max } : n.value)),
        lignes: axisValues,
        prix: prices,
        quincaillerie: hardware,
      }];
    }
  }
  return [];
}

// Extraction dédiée "Dos" : 7 groupes juxtaposés "Pour N Séparations" (0 à 6), chacun avec
// 3 plages de largeur, partageant la même bande de lignes "Hauteur".
function extractDos(grid, startRow, endRowExclusive) {
  for (let r = startRow; r < endRowExclusive; r++) {
    if (cellAt(grid, r, 3) !== "Pour") continue;
    const labelsRow = r + 1;
    const rangeRow = r + 3;
    const boundRow = r + 4;
    const heightStartRow = r + 5;
    const groups = [];
    for (let c = 3; c < 45; c += 3) {
      if (cellAt(grid, r, c) !== "Pour") break;
      const labelText = cellAt(grid, labelsRow, c);
      const m = typeof labelText === "string" && labelText.match(/(\d+)/);
      if (!m) break;
      groups.push({ separations: parseInt(m[1], 10), startCol: c });
    }
    if (!groups.length) continue;
    const blocks = [];
    for (const g of groups) {
      const cols = readColumnRanges(grid, rangeRow, boundRow, g.startCol, g.startCol + 2);
      if (!cols.length) continue;
      const { axisValues, prices } = readRowAxisAndPrices(grid, heightStartRow, cols);
      if (!axisValues.length) continue;
      blocks.push({
        profondeur: null,
        separations: g.separations,
        axe_colonnes: "largeur",
        colonnes: cols.map((c) => ({ min: c.min, max: c.max })),
        lignes: axisValues,
        prix: prices,
        quincaillerie: [],
      });
    }
    if (blocks.length) return blocks;
  }
  return [];
}

export function parsePricingWorkbook(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = wb.SheetNames.includes("Meuble") ? "Meuble" : wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const anchors = findSectionAnchors(grid);

  const sections = {};
  anchors.forEach((anchor, idx) => {
    const endRow = idx + 1 < anchors.length ? anchors[idx + 1].row : grid.length;
    let blocks;
    if (anchor.title === "Dos") {
      blocks = extractDos(grid, anchor.row, endRow);
    } else {
      blocks = extractCoupledBlocks(grid, anchor.row + 1, endRow);
      if (!blocks.length) blocks = extractSingleAxisBlock(grid, anchor.row + 1, endRow);
    }
    const key = anchor.title; // peut apparaître 2x pour "Tiroir" (variante M puis E)
    if (!sections[key]) sections[key] = [];
    sections[key].push(...blocks);
  });

  const pointCount = Object.values(sections).reduce(
    (sum, blocks) => sum + blocks.reduce((s, b) => s + b.prix.reduce((s2, row) => s2 + row.filter((v) => v !== null).length, 0), 0),
    0
  );

  return {
    sheet_name: sheetName,
    sections,
    meta: {
      imported_at: new Date().toISOString(),
      sections_found: Object.keys(sections),
      blocks_per_section: Object.fromEntries(Object.entries(sections).map(([k, v]) => [k, v.length])),
      extracted_points_count: pointCount,
    },
  };
}

// Recherche le prix précalculé pour une section/plage donnée. Si `depthRange` est omis,
// utilise le premier bloc profondeur disponible pour cette section (comportement par
// défaut documenté dans l'UI de test).
export function lookupPrice(parsedGrid, sectionTitle, { largeurMin, hauteurMin, depthMin } = {}) {
  const blocks = parsedGrid.sections[sectionTitle];
  if (!blocks || !blocks.length) return { error: `Section "${sectionTitle}" introuvable dans le fichier importé.` };
  const block = depthMin != null
    ? blocks.find((b) => b.profondeur && b.profondeur.min === depthMin)
    : blocks[0];
  if (!block) return { error: "Aucun bloc profondeur correspondant." };
  const colIdx = block.colonnes.findIndex((c) => (typeof c === "object" ? c.min === largeurMin : c === largeurMin));
  const rowIdx = block.lignes.findIndex((l) => l.min === hauteurMin);
  if (colIdx === -1 || rowIdx === -1) {
    return { error: "Plage largeur/hauteur introuvable dans ce bloc.", block };
  }
  return {
    price: block.prix[rowIdx][colIdx],
    profondeur: block.profondeur,
    largeur: block.colonnes[colIdx],
    hauteur: block.lignes[rowIdx],
    hardware_sample: block.quincaillerie.slice(0, 8),
  };
}
